import { z } from "zod";

import { AssignStatus, InclusionStatus } from "@/enum/list.enum";
import { MetaDataSchema } from "@/schemas/meta-data";

import { ListPersonOtherListsSchema, ListSchema } from "../../pages/List/schema/list";
import { PeopleSchema, ConversationSchema } from "../../pages/Apollo/schema/get-people";
import { ListPersonScheduleSchema } from "../../pages/List/schema/list-person-schedule.schema";
import {
	CompanyEnrichPreviewPersonSchema,
	CompanySearchPreviewItemSchema,
} from "../../pages/CompanyEnrich/schema/companyEnrich";
import { TrackerSignalResultSchema } from "../../pages/ProfileTracker/schema/trackerSignalResult";

// ─── ListPersonGlobalSearchInfoDTO ───────────────────────────────────────────

export const ListPersonGlobalSearchInfoDTOSchema = z.object({
	id: z.number().optional(),
	createdAt: z.coerce.date(),
	updatedAt: z.coerce.date(),
	campaignConfig: z.any().nullable().optional(),
	campaignId: z.string().nullable().optional(),
	prospect: PeopleSchema.optional(),
	assignStatus: z.nativeEnum(AssignStatus).optional(),
	inclusionStatus: z.nativeEnum(InclusionStatus).optional(),
	// z.lazy defers access to these list.ts schemas until validation runs, breaking the
	// module-eval circular import (list.ts → component/redux graph → search.slice → here).
	list: z.lazy(() => ListSchema).optional(),
	otherLists: z.array(z.lazy(() => ListPersonOtherListsSchema)).nullish(),
	conversations: z.array(ConversationSchema).nullish(),
	lastConversationAt: z.coerce.date().nullish(),
	trackerSignalResult: TrackerSignalResultSchema.nullish(),
	// The lead's outreach steps: what its card reads its status chip, channel and sender from
	// (PREC-4175). The API has always sent them; the schema dropped them.
	schedules: z.array(ListPersonScheduleSchema).nullish(),
});

export type ListPersonGlobalSearchInfoDTO = z.infer<typeof ListPersonGlobalSearchInfoDTOSchema>;

// ─── OrganizationGlobalSearchInfoDTO ─────────────────────────────────────────
// TODO: replace with the real OrganizationGlobalSearchInfoDTOSchema once available

export const OrganizationGlobalSearchInfoDTOSchema = z
	.object({
		id: z.number(),
		name: z.string().nullable().optional(),
		primaryDomain: z.string().nullable().optional(),
		logoUrl: z.string().nullable().optional(),
	})
	.passthrough();

export type OrganizationGlobalSearchInfoDTO = z.infer<typeof OrganizationGlobalSearchInfoDTOSchema>;

// ─── Shared helpers ───────────────────────────────────────────────────────────

export const PageDTOSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
	z.object({
		data: z.array(itemSchema),
		meta: MetaDataSchema,
	});

// IPaginatedList carries page + totalPages in addition to items + totalItems
// (confirmed by the unwrapList empty fallback: { items:[], page:1, totalPages:0, totalItems:0 })
export const IPaginatedListSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
	z.object({
		items: z.array(itemSchema),
		page: z.number(),
		totalPages: z.number(),
		totalItems: z.number(),
	});

// Global search enriches each company-enrich person with the full company record
// (matchedCompany), resolved on the backend from the person's matched/current domain.
// The email/phone fields start empty and are filled on-demand (websocket) when the user
// reveals a contact channel from the card.
export const GlobalSearchEnrichedPersonSchema = CompanyEnrichPreviewPersonSchema.extend({
	matchedCompany: CompanySearchPreviewItemSchema.nullable().optional(),
	prospectId: z.number().nullable().optional(),
	email: z.string().nullable().optional(),
	emailStatus: z.string().nullable().optional(),
	phone: z.string().nullable().optional(),
	phoneStatus: z.string().nullable().optional(),
});

export const IPersonSummarySchema = GlobalSearchEnrichedPersonSchema;
export type IPersonSummary = z.infer<typeof IPersonSummarySchema>;
export type IPaginatedList<T> = {
	items: T[];
	page: number;
	totalPages: number;
	totalItems: number;
};
export type PageDTO<T> = { data: T[]; meta: z.infer<typeof MetaDataSchema> };

// ─── Request ─────────────────────────────────────────────────────────────────
// GlobalSearchDto has no `page` param — only query/organizationId/domain/limit

// Industry filter (used inside companyFilterSearch / companyFilterSimilar)
const IndustryFilterSchema = z.object({
	name: z.string(),
	naicsCodes: z.array(z.number()),
});

const YearRangeSchema = z.object({
	min: z.number().optional(),
	max: z.number().optional(),
});

const CompanyFundingAmountRangeSchema = z.object({
	min: z.number().optional(),
	max: z.number().optional(),
});

const CompanyExcludeFiltersSchema = z.object({
	domains: z.array(z.string()).optional(),
	regions: z.array(z.string()).optional(),
	countries: z.array(z.string()).optional(),
	states: z.array(z.number()).optional(),
	cities: z.array(z.number()).optional(),
	type: z.array(z.string()).optional(),
	category: z.array(z.string()).optional(),
	employees: z.array(z.string()).optional(),
	revenue: z.array(z.string()).optional(),
	industries: z.array(IndustryFilterSchema).optional(),
	keywords: z.array(z.string()).optional(),
	technologies: z.array(z.string()).optional(),
	fundingRounds: z.array(z.string()).optional(),
});

const WorkforceGrowthFilterSchema = z.object({
	period: z.enum(["m6", "y1", "y2"]).nullable().optional(),
	department: z.string().nullable().optional(),
	minPercent: z.number().nullable().optional(),
	maxPercent: z.number().nullable().optional(),
});

const CompanySearchInputSchema = z.object({
	lists: z.array(z.string()).optional(),
	semanticQuery: z.string().optional(),
	semanticWeight: z.number().optional(),
	exclude: CompanyExcludeFiltersSchema.optional(),
	query: z.string().optional(),
	foundedYear: YearRangeSchema.optional(),
	fundingAmount: CompanyFundingAmountRangeSchema.optional(),
	fundingYear: YearRangeSchema.optional(),
	categoryOperator: z.enum(["And", "Or"]).optional(),
	keywordsOperator: z.enum(["And", "Or"]).optional(),
	technologiesOperator: z.enum(["And", "Or"]).optional(),
	require: z.array(z.string()).optional(),
	regions: z.array(z.string()).optional(),
	countries: z.array(z.string()).optional(),
	states: z.array(z.number()).optional(),
	cities: z.array(z.number()).optional(),
	type: z.array(z.string()).optional(),
	category: z.array(z.string()).optional(),
	employees: z.array(z.string()).optional(),
	revenue: z.array(z.string()).optional(),
	industries: z.array(IndustryFilterSchema).optional(),
	keywords: z.array(z.string()).optional(),
	technologies: z.array(z.string()).optional(),
	fundingRounds: z.array(z.string()).optional(),
	workforceGrowth: WorkforceGrowthFilterSchema.optional(),
});

const CompanySimilarInputSchema = CompanySearchInputSchema.extend({
	domains: z.array(z.string()),
	similarityWeight: z.number().optional(),
});

const StringIdNameSchema = z.object({
	id: z.string(),
	name: z.string(),
});

const NumberIdNameSchema = z.object({
	id: z.number(),
	name: z.string(),
});

const PersonSearchSourceSchema = z.object({
	query: z.string().optional(),
	positionQuery: z.array(z.string()).optional(),
	atCurrentCompanyAfter: z.string().optional(),
	atCurrentCompanyBefore: z.string().optional(),
	atCurrentPositionAfter: z.string().optional(),
	atCurrentPositionBefore: z.string().optional(),
	countries: z.array(StringIdNameSchema).optional(),
	domains: z.array(z.string()).optional(),
	seniority: z.array(z.string()).optional(),
	department: z.array(z.string()).optional(),
	education: z.object({ institutionNameQuery: z.array(z.string()).optional() }).optional(),
	companyFilterSearch: CompanySearchInputSchema.nullable().optional(),
	companyFilterSimilar: CompanySimilarInputSchema.nullable().optional(),
	companyCountries: z.array(StringIdNameSchema).optional(),
	companyStates: z.array(NumberIdNameSchema).optional(),
	companyCities: z.array(NumberIdNameSchema).optional(),
});

export const GlobalSearchRequestSchema = z.object({
	personSearchSource: PersonSearchSourceSchema.optional(),
	organizationId: z.number().optional(),
	limit: z.number().optional(),
});

export type GlobalSearchRequest = z.infer<typeof GlobalSearchRequestSchema>;

// ─── Response ────────────────────────────────────────────────────────────────

export const GlobalSearchResponseSchema = z.object({
	contacts: PageDTOSchema(ListPersonGlobalSearchInfoDTOSchema),
	companyEnrich: IPaginatedListSchema(IPersonSummarySchema),
	// null when the request is scoped to a specific organization (hasOrg=true)
	organizations: PageDTOSchema(OrganizationGlobalSearchInfoDTOSchema).nullable(),
});

export type GlobalSearchResponse = z.infer<typeof GlobalSearchResponseSchema>;

// ─── On-demand email/phone reveal ──────────────────────────────────────────────

export const GlobalSearchEnrichRequestSchema = z.object({
	profileInfoId: z.string(),
	name: z.string().optional(),
});

export type GlobalSearchEnrichRequest = z.infer<typeof GlobalSearchEnrichRequestSchema>;

export const GlobalSearchEnrichResponseSchema = z.object({
	profileInfoId: z.string(),
	status: z.string(),
});

export type GlobalSearchEnrichResponse = z.infer<typeof GlobalSearchEnrichResponseSchema>;

export const GlobalSearchContactChannel = z.enum(["EMAIL", "PHONE"]);

// Websocket payload pushed when an on-demand reveal resolves. `profileInfoId` matches
// the card's `id`, so the modal can update the right result without a prospectId.
export const GlobalSearchContactUpdateSchema = z.object({
	profileInfoId: z.string(),
	prospectId: z.number().nullable(),
	companyId: z.number(),
	channel: GlobalSearchContactChannel,
	name: z.string().nullable(),
	email: z.string().nullable(),
	emailStatus: z.string().nullable(),
	phone: z.string().nullable(),
	phoneStatus: z.string().nullable(),
});

export type GlobalSearchContactUpdate = z.infer<typeof GlobalSearchContactUpdateSchema>;
